import styled from '@emotion/styled';
import MicrositeHero from '~/components/MicrositeHero';
import { useListingContext } from '~/editor/plugin/ListingPlugin';
import { DEMO_PHOTOS } from '~/demo';
import { useState } from 'react';
import { useSections } from '~/hooks/useSections';
import { css_background } from '~/emotion/theme';

interface Hero2Props {
	tagline?: string;
	address2?: string;
}

/* ── layout ── */

const Wrapper = styled.div`
	position: relative;
	width: 100%;
	background: ${css_background()};
	color: ${(props) => props.theme.color.black.text.strong};
	aspect-ratio: 5 / 3;
	@media (max-width: ${(props) => props.theme.viewport.medium - 1}px) {
		height: 50%;
	}
	@media (min-width: ${(props) => props.theme.viewport.medium}px) {
		height: 100vh;
	}
`;

const HeroBackground = styled.div`
	inset: 0;
	position: absolute;
	z-index: 0;
`;

const TopGradient = styled.div`
	display: none;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 6rem;
	background: linear-gradient(to bottom, rgba(0, 0, 0, 0.02), transparent);
	@media (min-width: ${(props) => props.theme.viewport.medium}px) {
		display: block;
	}
`;

const BottomGradient = styled.div<{ hasListing: boolean }>`
	display: none;
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: ${({ hasListing }) => (hasListing ? '33.333%' : '25%')};
	background: linear-gradient(to top, ${({ hasListing }) => (hasListing ? 'black' : 'rgba(0, 0, 0, 0.04)')}, transparent);
	@media (min-width: ${(props) => props.theme.viewport.medium}px) {
		display: block;
	}
`;

/* ── inner content container ── */

const InnerContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	height: 100%;
	justify-content: space-between;
	max-width: 1536px;
	margin-left: auto;
	margin-right: auto;
	padding: 1rem;
	padding-top: 0;
`;

/* ── nav ── */

const Nav = styled.nav<{ loaded: boolean }>`
	padding: 2rem 1rem;
	height: 80px;
	transition-delay: 100ms;
	@media (min-width: ${(props) => props.theme.viewport.medium}px) {
		transition-duration: 1000ms;
		transition-property: all;
		opacity: ${(props) => (props.loaded ? 1 : 0)};
		transform: ${(props) => (props.loaded ? 'translateY(0)' : 'translateY(-200%)')};
	}
`;

const NavList = styled.ul`
	gap: 2rem;
	display: none;
	justify-content: space-around;
	align-items: center;
	width: 100%;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	font-weight: 500;
	@media (min-width: ${(props) => props.theme.viewport.medium}px) {
		display: flex;
	}
	@media (min-width: ${(props) => props.theme.viewport.large}px) {
		font-size: 1.125rem;
		line-height: 1.75rem;
	}
`;

const NavItem = styled.li`
	border-bottom: 1px solid rgba(255, 255, 255, 0);
	transition: all 300ms;
	&:hover {
		border-bottom-color: white;
	}
`;

/* ── bottom overlay ── */

const BottomOverlay = styled.div`
	position: absolute;
	bottom: 2rem;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	& > * + * {
		margin-top: 2rem;
	}
	@media (max-width: 767px) {
		display: none;
	}
`;

const TaglineBlock = styled.div`
	text-align: center;
	& > * + * {
		margin-top: 0.5rem;
	}
`;

const Tagline = styled.div`
	font-size: 1.5rem;
	line-height: 2rem;
	@media (min-width: 640px) {
		font-size: 1.875rem;
		line-height: 2.25rem;
	}
	@media (min-width: ${(props) => props.theme.viewport.large}px) {
		font-size: 3rem;
		line-height: 1;
	}
`;

const Address = styled.div`
	text-transform: uppercase;
	@media (min-width: 640px) {
		font-size: 1.25rem;
		line-height: 1.75rem;
	}
	@media (min-width: ${(props) => props.theme.viewport.large}px) {
		font-size: 1.5rem;
		line-height: 2rem;
	}
`;

/* ── description excerpt ── */

const DescriptionBlock = styled.div`
	font-size: 1.125rem;
	line-height: 2rem;
	font-weight: 500;
	text-align: center;
`;

const DescriptionClamp = styled.div`
	max-width: 56rem;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
	padding-left: 1.5rem;
	padding-right: 1.5rem;
`;

const MoreLink = styled.a`
	color: #d1d5db;
	text-decoration: underline;
	text-underline-offset: 4px;
	&:hover {
		color: white;
	}
`;

/* ── stats row ── */

const StatsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-around;
	font-size: 1.125rem;
	line-height: 1.75rem;
	text-align: center;
	width: 100%;
	max-width: 80rem;
	gap: 1.5rem;
	padding-left: 1.5rem;
	padding-right: 1.5rem;
`;

const StatValue = styled.div`
	font-size: 1.875rem;
	line-height: 2.25rem;
	font-weight: 600;
`;

const StatLabel = styled.div`
	font-weight: 500;
	text-transform: uppercase;
	color: #d1d5db;
`;

/* ── helpers ── */

function formatLotSize(lot_acres?: number, lot?: number) {
	if (lot_acres) {
		return `${lot_acres.toFixed(2)} acres`;
	}
	if (lot) {
		return `${lot.toLocaleString()} sqft`;
	}
	return null;
}

/* ── component ── */

export default function HeroBlockEta({ tagline, address2 }: Hero2Props) {
	const listing = useListingContext();
	const sections = useSections();

	const [loaded, setLoaded] = useState(false);

	const hasListing = !!listing;

	return (
		<Wrapper>
			<HeroBackground>
				<MicrositeHero image={DEMO_PHOTOS[0]} onLoad={() => setLoaded(true)} />
			</HeroBackground>

			<TopGradient />
			<BottomGradient hasListing={hasListing} />

			<InnerContainer>
				<Nav loaded={loaded}>
					<NavList>
						{sections.map((s) => (
							<NavItem key={s.id}>
								<a href={`#${s.id}`}>{s.name}</a>
							</NavItem>
						))}
					</NavList>
				</Nav>
			</InnerContainer>

			<BottomOverlay>
				<TaglineBlock>
					{tagline && <Tagline>{tagline}</Tagline>}
					{address2 && <Address>{address2}</Address>}
				</TaglineBlock>

				{listing?.body && (
					<DescriptionBlock>
						<DescriptionClamp>{listing.body}</DescriptionClamp>
						<MoreLink href="#description">More...</MoreLink>
					</DescriptionBlock>
				)}

				{listing && (
					<StatsRow>
						{listing.price_formatted && (
							<div>
								<StatValue>${listing.price_formatted}</StatValue>
								<StatLabel>Price</StatLabel>
							</div>
						)}
						{listing.beds != null && (
							<div>
								<StatValue>{listing.beds}</StatValue>
								<StatLabel>Bed</StatLabel>
							</div>
						)}
						{listing.baths != null && (
							<div>
								<StatValue>{listing.baths}</StatValue>
								<StatLabel>Bath</StatLabel>
							</div>
						)}
						{listing.garage != null && (
							<div>
								<StatValue>{listing.garage} Car</StatValue>
								<StatLabel>Garage</StatLabel>
							</div>
						)}
						{listing.sqft && (
							<div>
								<StatValue>{listing.sqft}</StatValue>
								<StatLabel>Sq. Ft.</StatLabel>
							</div>
						)}
						{listing.lot != null && (
							<div>
								<StatValue>{formatLotSize(listing.lot_acres, listing.lot)}</StatValue>
								<StatLabel>Lot Size</StatLabel>
							</div>
						)}
						{listing.built != null && (
							<div>
								<StatValue>{listing.built}</StatValue>
								<StatLabel>Year Built</StatLabel>
							</div>
						)}
					</StatsRow>
				)}
			</BottomOverlay>
		</Wrapper>
	);
}
